import { Box, Flex, Text, Loader, Button, Divider } from "@mantine/core";
import QRCode from "react-qr-code";
import { useState } from "react";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import { BankIdQrCodeProps } from "../../types";
import { buildBankIdUrl, openBankId } from "../../utils/bankIdRedirect";
import BankIDLogo from "../../assets/bank-id-logo.svg";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

export const BankIdQrCode = ({
  isLoading,
  qrData,
  autoStartToken,
  error = false,
  instructionText,
  buttonText,
  buttonLoading = false,
  processStatusInfo,
  nonce,
}: BankIdQrCodeProps) => {
  const isMobile = isMobileDevice();
  const resolvedButtonText =
    buttonText ?? (isMobile ? "Öppna mobilt BankID" : "BankID på denna enhet");
  const [showQR, setShowQR] = useState(!isMobile);
  const [bankIdClicked, setBankIdClicked] = useState(false);

  const bankIdUrl = buildBankIdUrl({ autoStartToken, nonce });

  // BankID app has been opened or user is entering PIN, disable the button
  const bankIdAppOpened =
    processStatusInfo === "started" || processStatusInfo === "userSign";

  const handleBankIdClick = () => {
    if (!bankIdUrl || bankIdAppOpened) return;
    setBankIdClicked(true);
    openBankId(bankIdUrl);
  };

  if (error) {
    return <Text>Det gick inte att ladda QR koden. Vänligen försök igen.</Text>;
  }

  if (showQR) {
    return (
      <Flex align="center" direction="column" gap="md">
        <Text ta="center" size="sm">
          {instructionText}
        </Text>

        {/* QR payload is a live BankID auth token - never record it */}
        <Box
          className={REPLAY_BLOCK_CLASS}
          style={{ display: "flex", justifyContent: "center" }}
        >
          {isLoading ? (
            <Loader role="status" aria-label="Laddar QR-kod" />
          ) : qrData ? (
            <QRCode
              value={qrData}
              role="img"
              aria-label="QR-kod för inloggning med BankID. Skanna koden med BankID-appen."
            />
          ) : null}
        </Box>

        <>
          <Divider w="100%" label="eller" labelPosition="center" />

          <Button
            fullWidth
            onClick={handleBankIdClick}
            loading={bankIdClicked || buttonLoading || isLoading}
            disabled={bankIdAppOpened}
            data-testid="bank-id-redirect"
          >
            <Flex component="span" align="center" gap="xs">
              <BankIDLogo aria-hidden="true" />
              {resolvedButtonText}
            </Flex>
          </Button>
        </>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="md">
      <Button
        fullWidth
        onClick={handleBankIdClick}
        loading={bankIdClicked || buttonLoading || isLoading}
        disabled={bankIdAppOpened}
        data-testid="bank-id-redirect"
      >
        <Flex component="span" align="center" gap="xs">
          <BankIDLogo aria-hidden="true" />
          {resolvedButtonText}
        </Flex>
      </Button>

      <Divider label="eller" labelPosition="center" />

      <Button variant="default" fullWidth onClick={() => setShowQR(true)}>
        BankID på annan enhet
      </Button>
    </Flex>
  );
};
